import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  commitTargetsImport,
  createTarget,
  deleteTarget,
  fetchTargetByPhone,
  fetchTargets,
  previewTargetsImport,
  type TargetFilters,
  type TargetListItem,
  type TargetWriteBody,
  updateTarget,
  verifyConfirm,
  verifyStart,
} from "../api/targets";

// ─── Phone detection helper ─────────────────────────────────────────────────────

/**
 * Returns true if `q` looks like a phone number (national or E.164).
 * Strips whitespace, dashes, parens, and dots before testing.
 */
export function looksLikePhone(q: string): boolean {
  const stripped = q.replace(/[\s\-().]/g, "");
  return /^\+?\d{7,15}$/.test(stripped);
}

// ─── Hooks ──────────────────────────────────────────────────────────────────────

export function useTargets(filters?: string | TargetFilters) {
  // Stable query key whether called with a bare string or a filter object.
  const key =
    typeof filters === "string" ? { q: filters || undefined } : filters ?? {};
  return useQuery({
    queryKey: ["targets", "list", key],
    queryFn: () => fetchTargets(filters),
  });
}

/**
 * Look up a single target by phone (E.164).
 * `enabled` should be `looksLikePhone(query)`.
 * retry:false because 404 is expected and should not spam.
 */
export function useTargetByPhone(phone: string, enabled: boolean) {
  return useQuery({
    queryKey: ["targets", "by-phone", phone],
    queryFn: () => fetchTargetByPhone(phone),
    enabled,
    retry: false,
  });
}

export function useUpdateTarget(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<TargetWriteBody>) => updateTarget(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["targets"] });
    },
  });
}

export function useCreateTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TargetWriteBody) => createTarget(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["targets"] });
    },
  });
}

export function useDeleteTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteTarget(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["targets"] });
    },
  });
}

/** Place the verification call for a test_only target (POST verify/start). */
export function useVerifyStart() {
  return useMutation({
    mutationFn: (id: string) => verifyStart(id),
  });
}

/**
 * Submit the spoken OTP code (POST verify/confirm). Always resolves 200 with
 * { verified, reason? }; on a verified success we invalidate the targets list
 * so the badge flips.
 */
export function useVerifyConfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, code }: { id: string; code: string }) =>
      verifyConfirm(id, code),
    onSuccess: (result) => {
      if (result.verified) {
        void qc.invalidateQueries({ queryKey: ["targets"] });
      }
    },
  });
}

export function useImportTargets() {
  const qc = useQueryClient();
  const preview = useMutation({
    mutationFn: previewTargetsImport,
  });
  const commit = useMutation({
    mutationFn: (token: string) => commitTargetsImport(token),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["targets"] });
    },
  });
  return { preview, commit };
}

// Re-export TargetListItem for convenience in consumers
export type { TargetListItem };
